import { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Button, Avatar, Typography } from 'antd';
import {
  MenuOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebarConfig } from '../hooks/useSidebarConfig';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

interface FeedbackLayoutProps {
  children: ReactNode;
}

const FeedbackLayout = ({ children }: FeedbackLayoutProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { themeConfig } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Load dynamic sidebar configuration from database
  const { projectMenuItems, systemMenuItems } = useSidebarConfig('feedback');

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        collapsedWidth="0"
        width={215}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1000,
          background: '#ffffff', // White theme
          borderRight: '1px solid #e5e7eb',
        }}
        trigger={null}
      >
        <div
          style={{
            height: '64px',
            background: themeConfig.primaryColor, // Use theme primary color
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            padding: '8px 12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                color: '#ffffff',
                fontSize: collapsed ? '14px' : '18px',
                fontWeight: 'bold',
              }}
            >
              {collapsed ? 'FB' : 'FEEDBACK'}
            </div>
          </div>
        </div>

        <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
          {/* Main Menu Items */}
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={projectMenuItems as MenuProps['items']}
            onClick={handleMenuClick}
            style={{
              borderRight: 0,
              flex: 1,
              marginBottom: 'auto',
              background: '#ffffff',
            }}
            theme="light"
          />

          {/* System Settings (with divider) */}
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={systemMenuItems as MenuProps['items']}
            onClick={handleMenuClick}
            style={{
              borderRight: 0,
              borderTop: '1px solid #e5e7eb',
              background: '#ffffff',
            }}
            theme="light"
          />

          {/* Profile Section */}
          {!collapsed && (
            <div
              style={{
                borderTop: '1px solid #e5e7eb',
                padding: '16px',
                background: '#f9fafb',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <Avatar
                  icon={<UserOutlined />}
                  size={40}
                  style={{
                    backgroundColor: themeConfig.primaryColor,
                    marginRight: '12px',
                    border: '2px solid #ffffff',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#f9fafb',
                      lineHeight: '20px',
                    }}
                  >
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      lineHeight: '16px',
                    }}
                  >
                    {user?.email}
                  </div>
                </div>
              </div>
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  height: '32px',
                  color: '#ef4444',
                  fontSize: '12px',
                }}
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </Sider>

      <AntLayout style={{ marginLeft: collapsed ? 0 : 215 }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 999,
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 40,
              height: 40,
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Text style={{ color: '#6b7280', fontSize: '14px' }}>
              Event Feedback Management
            </Text>
          </div>
        </Header>

        <Content
          style={{
            padding: '24px',
            background: '#f3f4f6',
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default FeedbackLayout;

